/**
 * The grid of the credit-limits page (masterplan credit-limits-v3, package A4; E14). Generic: no credit-limit logic and
 * no API; the page (A5, B4, B5) hands in columns and rows.
 *
 * Copied in parts from inventory-app `web/src/components/table/AnalysisTable.tsx` and `roving.ts` (read 2026-10-22):
 * the virtualised body with padding rows and a fixed row height (TanStack Virtual), the roving tab stop that is always a
 * rendered row, the rescue of a focused row the virtualiser removed, the header with the sort button, the caret only on
 * the sorted column and the resize handle on the sticky cell. What differs, and why:
 *
 * - **Sorting and filtering run in the browser** over the rows the page loaded (the overview loads all of them, §2), with
 *   this repo's funnel filter: `ui/ColumnFilter` + `hooks/useColumnFilters` (E14). Sort keys start with
 *   `ui/SortableHeader`'s `nextSort` (text ascending, figures descending); missing values sort last both ways.
 * - **Virtualised from 200 rows** (`VIRTUAL_FROM`, the A4 order; inventory uses 50 against a paged server list).
 * - **Widths, shown columns and sort** live in localStorage, one entry per grid (`useClimColumns`).
 * - **The row menu** is `ui/ContextMenu` through `ClimRowMenu`, not Radix: right-click, the menu key and Shift+F10.
 * - **Editing is a slot**: a column with `renderEditor` draws it in place of the cell while `editing` names the row and
 *   the column. F2 on a row and a double-click on an editable cell ask the page to start (`onEditRequest`); the page
 *   owns the state and the editor (B4, after budget-app-v2 `gridKeyboard.ts`). Keys typed inside an editor never move
 *   the row focus: the row handles only events whose target is the row itself.
 *
 * Keyboard: Tab reaches the grid through one row; ArrowUp/ArrowDown, PageUp/PageDown, Home and End move between rows;
 * Enter activates the row (`onRowActivate`); the header's sort buttons and funnels are buttons; the resize handle takes
 * ArrowLeft/ArrowRight (8 px, 40 with Shift) and Enter or a double-click resets it.
 */
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { ColumnFilter } from '../../ui/ColumnFilter';
import { nextSort } from '../../ui/SortableHeader';
import { isColumnFilterActive, useColumnFilters, type FilterColumn } from '../../../hooks/useColumnFilters';
import { useTopmostEscape } from '../../../hooks/useTopmostEscape';
import { ClimRowMenu, isMenuKey, keyboardMenuPoint, type ClimMenuAnchor, type ClimMenuItem } from './ClimRowMenu';
import { useClimColumns, type ClimColumnSpec, type ClimSort, type SortDirection } from './useClimColumns';

/** Virtualise from this many rows on (A4 order). Below it every row is in the DOM. */
export const VIRTUAL_FROM = 200;
/** The sticky header row (`--thead-h`): the virtual list starts below it. */
const HEAD_H = 40;
const DEFAULT_ROW_H = 48;
const NUDGE = 8;
const NUDGE_LARGE = 40;
/** A layout effect in the browser, a plain effect where there is no window (server render). */
const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export type CellValue = string | number | null | undefined;

export interface ClimColumn<Row> extends ClimColumnSpec {
    /** Header text; also the name of the sort button, the funnel and the resize handle. */
    label: string;
    /** Header tooltip: the full name where the label is short. */
    title?: string;
    /** Figures: right-aligned, sorted descending first, the funnel offers a from–to range. */
    numeric?: boolean;
    /** The value the column sorts and filters by. */
    value: (row: Row) => CellValue;
    /** The value the funnel lists, when it differs from `value` (a code shown as a name). */
    filterValue?: (row: Row) => CellValue;
    /** The cell's content; default: the value as text, a dash when it is missing. */
    render?: (row: Row) => ReactNode;
    /** Extra classes for the cell (`strong`, a tone). */
    cellClassName?: (row: Row) => string | undefined;
    /** The key column: strong, with the full text in `title`. */
    isKey?: boolean;
    /** false: no funnel. Default true. */
    filterable?: boolean;
    /** The editor slot (B4): drawn in place of the cell while `editing` names this row and column. */
    renderEditor?: (row: Row) => ReactNode;
    /** Whether this row's cell may be edited; default: whenever `renderEditor` is set. */
    editable?: (row: Row) => boolean;
}

export interface ClimGridLabels {
    /** «{shown} of {total}», e.g. «578 of 578 customers». */
    count: (shown: number, total: number) => string;
    filtersActive: (n: number) => string;
    clearFilters: string;
    columns: string;
    resetColumns: string;
    empty: string;
    resize: (label: string) => string;
    filter: { search: string; all: string; none: string; from: string; to: string; empty: string };
}

export const DEFAULT_LABELS: ClimGridLabels = {
    count: (shown, total) => `${shown.toLocaleString('de-CH')} of ${total.toLocaleString('de-CH')} rows`,
    filtersActive: n => (n === 1 ? '1 filter' : `${n} filters`),
    clearFilters: 'Clear filters',
    columns: 'Columns',
    resetColumns: 'Default columns',
    empty: 'No rows match the filters.',
    resize: label => `Resize ${label}`,
    filter: { search: 'Search…', all: 'Select all', none: 'Clear', from: 'From', to: 'To', empty: '(empty)' },
};

export interface ClimGridProps<Row> {
    /** Unique per grid: the localStorage entry of widths, columns and sort is `clim-grid:<gridId>`. */
    gridId: string;
    /** The grid's name for a screen reader: «Customers of Dolder-Bigler». */
    caption: string;
    columns: readonly ClimColumn<Row>[];
    rows: readonly Row[];
    rowKey: (row: Row) => string;
    defaultSort?: ClimSort | null;
    /** The entries of the row menu; without it the browser's own menu stays. */
    rowMenu?: (row: Row) => ClimMenuItem[];
    /** Enter on a row. */
    onRowActivate?: (row: Row) => void;
    /** The cell whose editor is shown (B4). */
    editing?: { rowKey: string; columnKey: string } | null;
    /** F2 on a row (first editable column) or a double-click on an editable cell. */
    onEditRequest?: (row: Row, columnKey: string) => void;
    /** A row drawn as selected (tint and bar), e.g. the one a side view shows. */
    selectedKey?: string | null;
    rowClassName?: (row: Row) => string | undefined;
    /** 48 (default) or 40 (compact). */
    rowHeight?: number;
    /** Left part of the toolbar above the grid (the page's own controls). */
    toolbar?: ReactNode;
    labels?: Partial<ClimGridLabels>;
}

// ---------------------------------------------------------------- pure parts (tested without a browser)

/** Missing: null, undefined, '' and NaN. */
function isMissing(v: CellValue): boolean {
    return v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));
}

/**
 * The rows in the sort's order. Missing values go last in both directions; two numbers compare as numbers, anything
 * else as text with a numeric-aware compare («Inv-2» before «Inv-10»). Stable: equal rows keep their order.
 */
export function sortRows<Row>(rows: readonly Row[], value: ((row: Row) => CellValue) | null, direction: SortDirection): Row[] {
    if (!value) return [...rows];
    const dir = direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const av = value(a);
        const bv = value(b);
        const aNil = isMissing(av);
        const bNil = isMissing(bv);
        if (aNil && bNil) return 0;
        if (aNil) return 1;
        if (bNil) return -1;
        const base = typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av).localeCompare(String(bv), 'de-CH', { numeric: true, sensitivity: 'base' });
        return base * dir;
    });
}

/** The next sort after a click on a column's header (`nextSort` of ui/SortableHeader: text asc, figures desc). */
export function nextClimSort(current: ClimSort | null, key: string, numeric: boolean): ClimSort {
    const next = nextSort({ key: current?.key ?? null, direction: current?.direction ?? 'asc' }, key, numeric);
    return { key, direction: next.direction };
}

/** The rendered rows of a virtualised body, as indexes (overscan included); null = every row is rendered. */
export interface RenderedRange { first: number; last: number }

/** The row with `tabIndex=0`: the active row when it is rendered, else the rendered row nearest to it; -1 without rows. */
export function tabStop(active: number, count: number, rendered: RenderedRange | null): number {
    if (count <= 0) return -1;
    const i = Math.max(0, Math.min(count - 1, active));
    if (!rendered) return i;
    const first = Math.max(0, Math.min(count - 1, rendered.first));
    const last = Math.max(first, Math.min(count - 1, rendered.last));
    return Math.max(first, Math.min(last, i));
}

/** The row the menu was opened on, by its key, among the rows on screen now; null when that row is gone. */
export function menuRowOf<Row>(rows: readonly Row[], rowKey: (row: Row) => string, key: string | null): Row | null {
    if (key === null) return null;
    return rows.find(r => rowKey(r) === key) ?? null;
}

function cellText(v: CellValue): string {
    if (isMissing(v)) return '–';
    return typeof v === 'number' ? v.toLocaleString('de-CH') : String(v);
}

// ---------------------------------------------------------------- the grid

export function ClimGrid<Row>(props: ClimGridProps<Row>) {
    const { gridId, caption, columns, rows, rowKey, defaultSort = null, rowMenu, onRowActivate, editing = null, onEditRequest, selectedKey = null, rowClassName, rowHeight = DEFAULT_ROW_H, toolbar } = props;
    const labels = useMemo<ClimGridLabels>(() => ({ ...DEFAULT_LABELS, ...props.labels, filter: { ...DEFAULT_LABELS.filter, ...props.labels?.filter } }), [props.labels]);
    const wrapRef = useRef<HTMLDivElement | null>(null);

    const cols = useClimColumns(columns, gridId, defaultSort);
    const visible = useMemo(() => columns.filter(c => !cols.hidden.has(c.key)), [columns, cols.hidden]);

    // ---- filter (funnels), then sort: both over the rows the page loaded
    const filterColumns = useMemo<FilterColumn<Row>[]>(
        () => columns.filter(c => c.filterable !== false).map(c => ({ key: c.key, accessor: c.filterValue ?? c.value, numeric: c.numeric })),
        [columns],
    );
    const { filtered, filters, setColumnFilter, activeCount, clearAll, uniqueValuesFor } = useColumnFilters(rows as Row[], filterColumns);
    // A column the person hides takes its filter with it: a filter nobody can see must not shorten the list.
    useEffect(() => {
        for (const key of Object.keys(filters)) if (cols.hidden.has(key)) setColumnFilter(key, null);
    }, [cols.hidden, filters, setColumnFilter]);

    const sortCol = cols.sort ? columns.find(c => c.key === cols.sort!.key && c.sortable !== false) ?? null : null;
    const sorted = useMemo(
        () => sortRows(filtered, sortCol ? sortCol.value : null, cols.sort?.direction ?? 'asc'),
        [filtered, sortCol, cols.sort?.direction],
    );

    // ---- virtualisation
    const virtual = sorted.length >= VIRTUAL_FROM;
    const virtualizer = useVirtualizer({
        count: sorted.length,
        getScrollElement: () => wrapRef.current,
        estimateSize: () => rowHeight,
        overscan: 12,
        enabled: virtual,
        scrollMargin: HEAD_H,
        scrollPaddingStart: HEAD_H,
    });
    useEffect(() => { virtualizer.measure(); }, [rowHeight, virtualizer]);
    const items = virtual ? virtualizer.getVirtualItems() : [];

    // ---- roving focus: one row in the tab order; under the virtualisation the nearest rendered row
    const [active, setActive] = useState(0);
    const focusPending = useRef<number | null>(null);
    const focusedRow = useRef<HTMLTableRowElement | null>(null);
    useEffect(() => { if (active >= sorted.length) setActive(Math.max(0, sorted.length - 1)); }, [sorted.length, active]);
    const rendered: RenderedRange | null = virtual ? (items.length ? { first: items[0]!.index, last: items[items.length - 1]!.index } : { first: 0, last: -1 }) : null;
    const stop = tabStop(active, sorted.length, rendered);
    const rowEl = (i: number) => wrapRef.current?.querySelector<HTMLTableRowElement>(`tbody tr[data-index="${i}"]`) ?? null;
    useEffect(() => {
        const i = focusPending.current;
        if (i === null) return;
        const tr = rowEl(i);
        if (tr) { tr.focus({ preventScroll: virtual }); focusPending.current = null; }
    });
    // A focused row the virtualiser took out of the DOM hands the focus to the tab stop before the browser drops it.
    useIsoLayoutEffect(() => {
        const lost = focusedRow.current;
        if (!lost || lost.isConnected || focusPending.current !== null) return;
        const now = document.activeElement;
        if (now && now !== document.body) { focusedRow.current = null; return; }
        const tr = stop >= 0 ? rowEl(stop) : null;
        focusedRow.current = null;
        if (tr) { tr.focus({ preventScroll: true }); setActive(stop); }
    });
    const move = (to: number) => {
        if (sorted.length === 0) return;
        const i = Math.max(0, Math.min(sorted.length - 1, to));
        setActive(i);
        focusPending.current = i;
        if (virtual) virtualizer.scrollToIndex(i, { align: 'auto' });
    };

    // ---- the row menu, remembered by the row's key
    const [menu, setMenu] = useState<ClimMenuAnchor | null>(null);
    const menuRow = menu ? menuRowOf(sorted, rowKey, menu.rowKey) : null;
    useEffect(() => { if (menu && !menuRow) setMenu(null); }, [menu, menuRow]);
    const closeMenu = useCallback(() => setMenu(null), []);
    const onBodyContextMenu = (e: ReactMouseEvent<HTMLTableSectionElement>) => {
        if (!rowMenu) return;
        const tr = (e.target as Element).closest?.('tr[data-index]') as HTMLTableRowElement | null;
        const i = tr ? Number(tr.dataset.index) : NaN;
        const row = Number.isInteger(i) ? sorted[i] : undefined;
        // A padding row or the empty line has no row: no menu, and not the browser's either.
        e.preventDefault();
        if (row === undefined) return;
        setActive(i);
        setMenu({ rowKey: rowKey(row), x: e.clientX, y: e.clientY });
    };

    const firstEditable = (row: Row) => visible.find(c => c.renderEditor && (c.editable ? c.editable(row) : true))?.key ?? null;

    const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>, i: number) => {
        // Keys typed in an editor or a button inside the row are theirs, not the row's.
        if (e.target !== e.currentTarget) return;
        const row = sorted[i]!;
        const page = Math.max(1, Math.floor((wrapRef.current?.clientHeight ?? 400) / rowHeight) - 2);
        if (e.key === 'ArrowDown') { e.preventDefault(); move(i + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); move(i - 1); }
        else if (e.key === 'PageDown') { e.preventDefault(); move(i + page); }
        else if (e.key === 'PageUp') { e.preventDefault(); move(i - page); }
        else if (e.key === 'Home') { e.preventDefault(); move(0); }
        else if (e.key === 'End') { e.preventDefault(); move(sorted.length - 1); }
        else if (e.key === 'Enter' && onRowActivate) { e.preventDefault(); onRowActivate(row); }
        else if (e.key === 'F2' && onEditRequest) {
            const key = firstEditable(row);
            if (key) { e.preventDefault(); onEditRequest(row, key); }
        } else if (rowMenu && isMenuKey(e)) {
            e.preventDefault();
            const at = keyboardMenuPoint(e.currentTarget.getBoundingClientRect());
            setMenu({ rowKey: rowKey(row), ...at });
        }
    };

    // ---- header
    const clickSort = (col: ClimColumn<Row>) => {
        if (cols.shouldSuppressClick()) return;
        cols.setSort(nextClimSort(cols.sort, col.key, !!col.numeric));
    };
    const ariaSort = (col: ClimColumn<Row>) => (cols.sort?.key === col.key ? (cols.sort.direction === 'asc' ? 'ascending' : 'descending') : undefined);

    const tableWidth = cols.totalWidthOf(visible.map(c => c.key));

    const renderRow = (row: Row, i: number) => {
        const key = rowKey(row);
        const selected = key === selectedKey || (menu !== null && menu.rowKey === key);
        const cls = [selected ? 'selected' : '', rowClassName?.(row) ?? ''].filter(Boolean).join(' ') || undefined;
        return (
            <tr
                key={key}
                data-index={i}
                data-key={key}
                tabIndex={i === stop ? 0 : -1}
                className={cls}
                onFocus={e => { if (e.target !== e.currentTarget) return; focusedRow.current = e.currentTarget; if (active !== i) setActive(i); }}
                onBlur={e => {
                    // A real blur forgets the row; a row the virtualiser removed keeps it for the rescue above.
                    const el = e.currentTarget;
                    window.setTimeout(() => { if (el.isConnected && focusedRow.current === el) focusedRow.current = null; }, 0);
                }}
                onKeyDown={e => onRowKey(e, i)}
            >
                {visible.map(col => {
                    const isEditing = !!col.renderEditor && editing !== null && editing.rowKey === key && editing.columnKey === col.key;
                    const canEdit = !!col.renderEditor && !!onEditRequest && (col.editable ? col.editable(row) : true);
                    const v = col.value(row);
                    const tdClass = [col.numeric ? 'n' : '', col.isKey ? 'key' : '', isMissing(v) && !col.render ? 'dash' : '', isEditing ? 'editing' : '', col.cellClassName?.(row) ?? '']
                        .filter(Boolean).join(' ') || undefined;
                    return (
                        <td
                            key={col.key}
                            className={tdClass}
                            title={col.isKey && !isMissing(v) ? String(v) : undefined}
                            onDoubleClick={canEdit && !isEditing ? () => onEditRequest!(row, col.key) : undefined}
                        >
                            {isEditing ? col.renderEditor!(row) : col.render ? col.render(row) : cellText(v)}
                        </td>
                    );
                })}
            </tr>
        );
    };

    // Item offsets include the scroll margin (the header); the padding rows live inside the body.
    const pad = virtual && items.length
        ? { top: items[0]!.start - HEAD_H, bottom: virtualizer.getTotalSize() - (items[items.length - 1]!.end - HEAD_H) }
        : { top: 0, bottom: 0 };

    return (
        <div className="clim-grid">
            <div className="toolbar">
                {toolbar}
                <span className="tb-grow" />
                <span className="count" aria-live="polite">
                    {labels.count(sorted.length, rows.length)}
                    {activeCount > 0 ? ` · ${labels.filtersActive(activeCount)}` : ''}
                </span>
                {activeCount > 0 ? <button type="button" className="lnk" onClick={clearAll}>{labels.clearFilters}</button> : null}
                <ColumnPicker columns={columns} hidden={cols.hidden} setVisible={cols.setVisible} reset={cols.resetVisibility} labels={labels} gridId={gridId} />
            </div>
            <div ref={wrapRef} className={`tbl-wrap${rowHeight < DEFAULT_ROW_H ? ' compact' : ''}${cols.resizingKey ? ' resizing' : ''}`} data-grid={gridId}>
                <table className="tbl" aria-label={caption} aria-rowcount={sorted.length + 1} style={{ width: tableWidth, minWidth: '100%' }}>
                    <colgroup>
                        {visible.map(c => <col key={c.key} style={{ width: cols.widthOf(c.key) }} />)}
                    </colgroup>
                    <thead>
                        <tr>
                            {visible.map(col => {
                                const sortable = col.sortable !== false;
                                const filterable = col.filterable !== false;
                                const on = isColumnFilterActive(filters[col.key]);
                                const width = cols.widthOf(col.key);
                                return (
                                    <th key={col.key} scope="col" className={col.numeric ? 'n' : undefined} aria-sort={sortable ? ariaSort(col) : undefined} title={col.title}>
                                        <div className="th-in">
                                            {sortable ? (
                                                <button type="button" className="sort" onClick={() => clickSort(col)}>
                                                    <span className="lbl">{col.label}</span>
                                                    {cols.sort?.key === col.key ? <span className="caret" aria-hidden="true">{cols.sort.direction === 'asc' ? '▲' : '▼'}</span> : null}
                                                </button>
                                            ) : <span className="th-plain">{col.label}</span>}
                                            {filterable ? (
                                                <span className={`colfilter${on ? ' on' : ''}`}>
                                                    <ColumnFilter
                                                        label={col.label}
                                                        values={uniqueValuesFor(col.key)}
                                                        state={filters[col.key]}
                                                        onChange={next => setColumnFilter(col.key, next)}
                                                        numeric={col.numeric}
                                                        active={on}
                                                        labels={labels.filter}
                                                    />
                                                </span>
                                            ) : null}
                                        </div>
                                        <span
                                            className="col-resize"
                                            role="separator"
                                            aria-orientation="vertical"
                                            aria-label={labels.resize(col.label)}
                                            aria-valuenow={width}
                                            aria-valuemin={60}
                                            aria-valuemax={900}
                                            tabIndex={0}
                                            onMouseDown={e => { e.preventDefault(); e.stopPropagation(); cols.startResize(col.key, e.clientX); }}
                                            onDoubleClick={() => cols.resetWidth(col.key)}
                                            onKeyDown={e => {
                                                const step = e.shiftKey ? NUDGE_LARGE : NUDGE;
                                                if (e.key === 'ArrowLeft') { e.preventDefault(); cols.nudge(col.key, -step); }
                                                else if (e.key === 'ArrowRight') { e.preventDefault(); cols.nudge(col.key, step); }
                                                else if (e.key === 'Enter') { e.preventDefault(); cols.resetWidth(col.key); }
                                            }}
                                        />
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody onContextMenu={rowMenu ? onBodyContextMenu : undefined}>
                        {sorted.length === 0 ? (
                            <tr className="empty-row"><td colSpan={Math.max(1, visible.length)}>{labels.empty}</td></tr>
                        ) : virtual ? (
                            <>
                                {pad.top > 0 ? <tr className="vpad" aria-hidden="true"><td colSpan={visible.length} style={{ height: pad.top }} /></tr> : null}
                                {items.map(it => renderRow(sorted[it.index]!, it.index))}
                                {pad.bottom > 0 ? <tr className="vpad" aria-hidden="true"><td colSpan={visible.length} style={{ height: pad.bottom }} /></tr> : null}
                            </>
                        ) : sorted.map(renderRow)}
                    </tbody>
                </table>
            </div>
            {menu && menuRow !== null && rowMenu ? <ClimRowMenu anchor={menu} items={rowMenu(menuRow)} onClose={closeMenu} /> : null}
        </div>
    );
}

// ---------------------------------------------------------------- the column picker

function ColumnPicker<Row>({ columns, hidden, setVisible, reset, labels, gridId }: {
    columns: readonly ClimColumn<Row>[];
    hidden: ReadonlySet<string>;
    setVisible: (key: string, visible: boolean) => void;
    reset: () => void;
    labels: ClimGridLabels;
    gridId: string;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const close = useCallback(() => { setOpen(false); buttonRef.current?.focus(); }, []);
    useTopmostEscape(open, close);
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);
    const hideable = columns.filter(c => c.hideable !== false);
    if (hideable.length === 0) return null;
    const popId = `clim-colpick-${gridId}`;
    return (
        <div className="colpick" ref={ref}>
            <button ref={buttonRef} type="button" className="btn secondary sm" aria-expanded={open} aria-controls={popId} onClick={() => setOpen(o => !o)}>
                {labels.columns}
            </button>
            {open ? (
                <div className="popover" id={popId} role="group" aria-label={labels.columns}>
                    {hideable.map(c => (
                        <label key={c.key} className="pick">
                            <input type="checkbox" className="check" checked={!hidden.has(c.key)} onChange={e => setVisible(c.key, e.target.checked)} />
                            <span>{c.label}</span>
                        </label>
                    ))}
                    <button type="button" className="menu-item" onClick={reset}>{labels.resetColumns}</button>
                </div>
            ) : null}
        </div>
    );
}
